import { View, Text, Button, Card } from "@src/components";
import { router, Stack, useLocalSearchParams } from "expo-router";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { ActivityIndicator, ScrollView } from "react-native";
import { useCallback, useMemo } from "react";
import { Section } from "@src/features/settings/components/Section";
import { Icon } from "@src/components/Icon";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import type { SiteUser } from "@src/features/settings/data/mock-sites";
import { SpecificCameraAccessList } from "@src/features/settings/components/SpecificCameraAccessList";
import { CameraAdminsList } from "@src/features/settings/components/CameraAdminsList";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { CameraUsersList } from "@src/features/settings/components/CameraUsersList";
import { useAccessManagement, useAddCameraPermission } from "@visioni/a3s-access/app";
import type { CameraRole, Role } from "@visioni/a3s-access/schemas";
import { parseSiteRole } from "@src/features/settings/utils";
import { isWeb } from "@src/utils/web-helpers";
import { WebLayout } from "@src/components/web/WebLayout.web";

const toUiRole = (role: CameraRole): SiteUser["role"] => (role === "camera_manager" ? "manager" : "viewer");
const toCameraRole = (role: SiteUser["role"]): CameraRole => (role === "manager" ? "camera_manager" : "camera_viewer");

export default function ManageAccessScreen() {
  const { theme } = useUnistyles();
  const { headerLeft } = useHeaderBackButton();
  const { cameraNickname, cameraAssetPart, cameraIdPart, siteId, cameraKey } = useLocalSearchParams<{
    cameraNickname?: string;
    cameraAssetPart?: string;
    cameraIdPart?: string;
    siteId?: string;
    cameraKey?: string;
  }>();

  const cameraId = siteId && cameraKey ? `${siteId}/${cameraKey}` : null;

  const { data, isLoading } = useAccessManagement();
  const addCameraPermission = useAddCameraPermission();

  const cameraUsers = useMemo<SiteUser[]>(() => {
    if (!data || !cameraId) return [];
    const byId = new Map(data.users.map((u) => [String(u.id), u]));
    return data.cameras.flatMap((assignment) => {
      if (assignment.camera !== cameraId) return [];
      const u = byId.get(String(assignment.user));
      if (!u) return [];
      return [
        {
          id: String(u.id),
          name: [u.givenName, u.familyName].filter(Boolean).join(" ") || u.email,
          email: u.email,
          phoneNumber: u.phoneNumber ?? "",
          role: toUiRole(assignment.role),
        },
      ];
    });
  }, [data, cameraId]);

  const tenantAdmins = useMemo<SiteUser[]>(() => {
    if (!data) return [];
    const adminIds = new Set(data.roles.filter((r) => r.role === "tenant_admin").map((r) => String(r.user)));
    return data.users
      .filter((u) => adminIds.has(String(u.id)))
      .map((u) => ({
        id: String(u.id),
        name: [u.givenName, u.familyName].filter(Boolean).join(" ") || u.email,
        email: u.email,
        phoneNumber: u.phoneNumber ?? "",
        role: "manager",
      }));
  }, [data]);

  const siteWideUsers = useMemo<SiteUser[]>(() => {
    if (!data || !siteId) return [];
    const byId = new Map(data.users.map((u) => [String(u.id), u]));
    return data.roles.flatMap((r) => {
      const parsed = parseSiteRole(r.role as Role);
      if (!parsed || parsed.siteId !== siteId) return [];
      const u = byId.get(String(r.user));
      if (!u) return [];
      return [
        {
          // Composite id so the same user can appear once as manager and once as viewer without key collisions.
          id: `${String(u.id)}:${parsed.role}`,
          name: [u.givenName, u.familyName].filter(Boolean).join(" ") || u.email,
          email: u.email,
          phoneNumber: u.phoneNumber ?? "",
          role: parsed.role,
        },
      ];
    });
  }, [data, siteId]);

  const headerTitle = useCallback(() => {
    return (
      <View flex={1} flexDirection="row" alignItems="center" justifyContent="space-between" pr="7xl">
        <Text textStyle="lg/semibold" color="text">
          Users & Permissions
        </Text>
        <View alignItems="center" gap="xxs">
          {cameraNickname ?
            <>
              <Text textStyle="xxs/medium">{cameraNickname}</Text>
              {cameraIdPart ?
                <Text textStyle="xxs/medium" color="textMuted">
                  {cameraIdPart}
                </Text>
              : null}
            </>
          : <>
              <Text textStyle="xxs/medium">{cameraAssetPart}</Text>
              {cameraIdPart ?
                <Text textStyle="xxs/medium" color="textMuted">
                  {cameraIdPart}
                </Text>
              : null}
            </>
          }
        </View>
      </View>
    );
  }, [cameraNickname, cameraAssetPart, cameraIdPart]);

  const handleNavigateToAddUsers = useCallback(() => {
    if (!siteId || !cameraKey) return;
    router.push({
      pathname: "/contacts/add-users-to-camera",
      params: {
        siteId,
        cameraKey,
        cameraNickname: cameraNickname ?? "",
        cameraAssetPart: cameraAssetPart ?? "",
      },
    });
  }, [siteId, cameraKey, cameraNickname, cameraAssetPart]);

  const handleNavigateToCameraAccess = useCallback((userId: string) => {
    router.push(`/settings/site-access/camera-access/${encodeURIComponent(userId)}`);
  }, []);

  // siteWideUsers entries use a composite id `${userId}:${role}` to keep a user that is both
  // manager and viewer rendered twice. Strip the role suffix before navigating.
  const handleSiteWideUserPress = useCallback(
    (compositeId: string) => {
      const [rawUserId] = compositeId.split(":");
      handleNavigateToCameraAccess(rawUserId);
    },
    [handleNavigateToCameraAccess],
  );

  const handleCameraUserRoleChange = useCallback(
    (userId: string, role: SiteUser["role"]) => {
      if (!cameraId) return;
      const target = cameraUsers.find((u) => u.id === userId);
      addCameraPermission.mutate(
        { user: userId, camera: cameraId, role: toCameraRole(role) },
        {
          onSuccess: () => {
            if (target) {
              showSuccessToast({
                message: "Role updated",
                description: `${target.name} is now ${role.charAt(0).toUpperCase() + role.slice(1)}`,
              });
            }
          },
          onError: () => showErrorToast({ message: "Failed to update role" }),
        },
      );
    },
    [cameraId, cameraUsers, addCameraPermission],
  );

  return (
    <>
      <Stack.Screen
        options={
          isWeb ?
            { headerShown: false }
          : {
              title: "Users & Permissions",
              headerTitle,
              headerLeft,
            }
        }
      />
      {isWeb ?
        <WebLayout>
          <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
            {/* Web-only header showing the camera identity above the cards. The native screen
                tucks this into the navigation bar via `headerTitle`; on web the bar is hidden
                so we surface the same information here. */}
            <View flexDirection="row" alignItems="center" justifyContent="space-between" mb="3xl">
              <Text textStyle="lg/semibold" color="text">
                Users & Permissions
              </Text>
              {(cameraNickname || cameraAssetPart) && (
                <View alignItems="flex-end" gap="xxs">
                  <Text textStyle="sm/medium" color="text">
                    {cameraNickname || cameraAssetPart}
                  </Text>
                  {cameraIdPart ?
                    <Text textStyle="xs/regular" color="textMuted">
                      {cameraIdPart}
                    </Text>
                  : null}
                </View>
              )}
            </View>

            <View gap="3xl" flex={1} flexDirection="row" alignItems="flex-start">
              <Card style={{ flexBasis: "50%" }}>
                <Section title="Camera access" subtitle="These users only have access to this camera.">
                  {isLoading ?
                    <ActivityIndicator style={{ paddingVertical: theme.spacing["3xl"] }} />
                  : cameraUsers.length > 0 ?
                    <SpecificCameraAccessList
                      users={cameraUsers}
                      onRoleChange={handleCameraUserRoleChange}
                      onPressItem={handleNavigateToCameraAccess}
                    />
                  : <Text
                      textStyle="sm/regular"
                      color="textMuted"
                      style={{ textAlign: "center", paddingVertical: theme.spacing["3xl"] }}
                    >
                      No users found
                    </Text>
                  }
                  <Button
                    size="md"
                    variant="primary"
                    label="Add someone"
                    iconRight={<Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.white.default} />}
                    onPress={handleNavigateToAddUsers}
                    style={styles.button}
                  />
                </Section>
              </Card>

              <Card style={{ flexBasis: "50%" }}>
                {/* On native these two sections are nested inside a parent "Site-wide access" Section.
                    On web the wider viewport lets us flatten them while keeping the same group label. */}
                <Section title="Site-wide access" subtitle="These users have access to all cameras on the site.">
                  <Section title="Organisation admins">
                    <CameraAdminsList users={tenantAdmins} onPress={handleNavigateToCameraAccess} />
                  </Section>
                  <Section title="Users" style={{ marginTop: theme.spacing["3xl"] }}>
                    <CameraUsersList users={siteWideUsers} onPress={handleSiteWideUserPress} />
                  </Section>
                </Section>
              </Card>
            </View>
          </ScrollView>
        </WebLayout>
      : <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
          <Section title="Camera access" subtitle="These users only have access to this camera.">
            {isLoading ?
              <ActivityIndicator style={{ paddingVertical: theme.spacing["3xl"] }} />
            : cameraUsers.length > 0 ?
              <SpecificCameraAccessList
                users={cameraUsers}
                onRoleChange={handleCameraUserRoleChange}
                onPressItem={handleNavigateToCameraAccess}
              />
            : <Text
                textStyle="sm/regular"
                color="textMuted"
                style={{ textAlign: "center", paddingVertical: theme.spacing["3xl"] }}
              >
                No users found
              </Text>
            }
            <Button
              size="md"
              variant="primary"
              label="Add someone"
              iconRight={<Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.white.default} />}
              onPress={handleNavigateToAddUsers}
              style={styles.button}
            />
          </Section>
          <View style={styles.separator} />
          <Section title="Site-wide access" subtitle="These users have access to all cameras on the site.">
            <Section title="Organisation admins">
              <CameraAdminsList users={tenantAdmins} onPress={handleNavigateToCameraAccess} />
            </Section>
            <Section title="Users" style={{ marginTop: theme.spacing["3xl"] }}>
              <CameraUsersList users={siteWideUsers} onPress={handleSiteWideUserPress} />
            </Section>
          </Section>
        </ScrollView>
      }
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
    padding: theme.spacing["3xl"],
    gap: theme.spacing["6xl"],
    paddingBottom: theme.spacing["12xl"],
  },

  button: {
    marginTop: theme.spacing["3xl"],
  },
  separator: {
    height: 1,
    backgroundColor: theme.colors.borderCard,
  },
}));
